import React, { useState } from 'react';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import PrescriptionViewer from './PrescriptionViewer';
import CertificateViewer from './CertificateViewer';
import { Eye, Download, Printer, Edit, XCircle, History, Search, FlaskConical } from 'lucide-react';
import './PrescriptionHistory.css';

function PrescriptionHistory({ 
  prescriptions = [], 
  certificates = [], 
  onEdit, 
  onCancel, 
  initialFilter = 'all', 
  hasPharmacy = true, 
  hasLab = true 
}) {
  const [filterStatus, setFilterStatus] = useState(initialFilter);
  const [searchPatient, setSearchPatient] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewingPrescription, setViewingPrescription] = useState(null);
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [viewerMode, setViewerMode] = useState('view');

  const [page, setPage] = useState(1);
  const [goToPageInput, setGoToPageInput] = useState('');

  // Reset page to 1 when filters change
  React.useEffect(() => {
    setPage(1);
  }, [filterStatus, searchPatient, dateFrom, dateTo]);

  // Merge and sort all records
  const allRecords = [
    ...prescriptions.map(p => ({ ...p, recordType: 'prescription' })),
    ...certificates.map(c => ({ ...c, recordType: 'certificate', status: 'certificate' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredRecords = allRecords.filter(record => {
    let matchesStatus = filterStatus === 'all' || record.status === filterStatus;

    const matchesPatient = !searchPatient ||
      (record.patient_name && record.patient_name.toLowerCase().includes(searchPatient.toLowerCase()));
    const recordDate = new Date(record.created_at);
    const matchesDateFrom = !dateFrom || recordDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || recordDate <= new Date(dateTo);

    return matchesStatus && matchesPatient && matchesDateFrom && matchesDateTo;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const targetPage = parseInt(goToPageInput);
    if (targetPage >= 1 && targetPage <= totalPages) {
      setPage(targetPage);
      setGoToPageInput('');
    } else {
      toast.error(`Please enter a valid page between 1 and ${totalPages}`);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      if (!pages.includes(totalPages)) pages.push(totalPages);
    }

    return pages.map((p, idx) => {
      if (p === '...') {
        return <span key={`dots-${idx}`} className="pagination-dots">...</span>;
      }
      return (
        <button
          key={`page-${p}`}
          onClick={() => setPage(p)}
          className={`page-num-btn ${page === p ? 'active' : ''}`}
        >
          {p}
        </button>
      );
    });
  };

  async function handleViewRecord(record) {
    if (record.recordType === 'certificate') {
      setViewingCertificate(record);
      return;
    }

    const prescription = record;
    if (prescription.status === 'awaiting_review') {
      try {
        const { error } = await supabase
          .from('prescriptions')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .eq('id', prescription.id);

        if (error) throw error;
        toast.success('Prescription marked as completed after review.');
        prescription.status = 'completed';
      } catch (err) {
        toast.error('Failed to mark as completed: ' + err.message);
      }
    }
    setViewerMode('view');
    setViewingPrescription(prescription);
  }

  function handleDownload(record) {
    if (record.recordType === 'certificate') {
      setViewingCertificate(record);
      // CertificateViewer handles download internally via its button, 
      // but for consistency we could trigger it. 
      // For now, opening the viewer is enough as it has the download btn.
      return;
    }
    setViewerMode('download');
    setViewingPrescription(record);
  }

  function handlePrint(record) {
    if (record.recordType === 'certificate') {
      setViewingCertificate(record);
      return;
    }
    setViewerMode('print');
    setViewingPrescription(record);
  }

  function formatStatus(status) {
    const statusMap = {
      'draft': 'Draft',
      'in_progress': 'In Progress',
      'checked_in': 'Checked In',
      'pending': 'Pending',
      'sent_to_both': 'Sent to Pharmacy & Lab',
      'sent_to_pharmacy': 'Sent to Pharmacy',
      'sent_to_lab': 'Sent to Lab',
      'sample_collected': 'Sample Collected',
      'in_progress_lab': 'Testing In Progress',
      'report_uploaded': 'Report Uploaded',
      'awaiting_review': 'Awaiting Review',
      'revised': 'Revised',
      'completed': 'Completed',
      'dispensed': 'Dispensed',
      'cancelled': 'Cancelled',
      'certificate': 'Certificate'
    };
    return statusMap[status] || status;
  }

  return (
    <div className="prescription-history-container">
      <div className="history-filters-row">
        <div className="history-search-wrapper">
          <Search size={18} className="history-search-icon" />
          <input
            type="text"
            placeholder="Search patient"
            value={searchPatient}
            onChange={(e) => setSearchPatient(e.target.value)}
          />
          {searchPatient && (
            <button type="button" className="clear-search-btn-v3" onClick={() => setSearchPatient('')}>×</button>
          )}
        </div>

        <div className="history-filter-select-wrapper">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Records</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="certificate">Certificates</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="history-date-filter-group">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>

        <div className="history-date-filter-group">
          <label>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        {(searchPatient || filterStatus !== 'all' || dateFrom || dateTo) && (
          <button
            className="btn-clear-history-filters"
            onClick={() => {
              setFilterStatus('all');
              setSearchPatient('');
              setDateFrom('');
              setDateTo('');
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div className="results-count">
        Showing {filteredRecords.length} of {allRecords.length} records
      </div>

      <div className="history-content-scrollable">
        {/* Desktop Table View */}
        <div className="history-table-container desktop-view">
          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Type / Status</th>
                <th>Primary Diagnosis / Purpose</th>
                <th style={{ textAlign: 'right', width: '220px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((record) => (
                <tr 
                  key={record.id} 
                  onClick={() => handleViewRecord(record)}
                  className={`clickable-history-row ${record.recordType === 'certificate' ? 'certificate-row' : ''}`}
                >
                  <td>
                      <div className="date-cell">
                          <span className="d-main">{new Date(record.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
                          <span className="d-year">{new Date(record.created_at).getFullYear()}</span>
                      </div>
                  </td>
                  <td>
                    <div className="patient-cell">
                      <span className="p-name">{record.patient_name}</span>
                      <span className="p-details">{record.patient_age}y • {record.patient_gender}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-pill status-${record.status}`}>
                      {record.recordType === 'certificate' ? 'Medical Certificate' : formatStatus(record.status)}
                    </span>
                  </td>
                  <td className="diagnosis-cell">
                    {record.recordType === 'certificate' ? record.purpose : (record.diagnosis || 'N/A')}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="prescription-btn-group" style={{ justifyContent: 'flex-end' }}>
                      {record.lab_status === 'report_uploaded' && (
                        <a
                          href={`${window.location.origin}/app/report/${record.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-btn p-btn-lab"
                          title="View Lab Report"
                          style={{ textDecoration: 'none', color: '#059669', borderColor: '#a7f3d0', background: '#ecfdf5' }}
                        >
                          <FlaskConical size={16} />
                        </a>
                      )}
                      <button
                        className="p-btn p-btn-view"
                        onClick={() => handleViewRecord(record)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      
                      <button
                        className="p-btn p-btn-download"
                        onClick={() => handleDownload(record)}
                        title="Download PDF"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="p-btn p-btn-print"
                        onClick={() => handlePrint(record)}
                        title="Print"
                      >
                        <Printer size={16} />
                      </button>

                      {record.recordType === 'prescription' && (
                        <>
                          {!['cancelled', 'dispensed'].includes(record.status) && (
                            <button
                              className={`p-btn ${['draft', 'in_progress'].includes(record.status) ? 'p-btn-continue' : 'p-btn-edit'}`}
                              onClick={() => onEdit && onEdit(record)}
                              title={['draft', 'in_progress'].includes(record.status) ? "Continue" : "Edit"}
                            >
                              {['draft', 'in_progress'].includes(record.status) ? (
                                <>
                                  <Edit size={12} style={{ marginRight: '4px' }} />
                                  <span style={{ fontWeight: 600 }}>Continue</span>
                                </>
                              ) : (
                                <Edit size={16} />
                              )}
                            </button>
                          )}
                          
                          {!['cancelled', 'completed', 'dispensed'].includes(record.status) && (
                            <button
                              className="p-btn p-btn-cancel"
                              onClick={() => onCancel && onCancel(record.id)}
                              title="Cancel"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-view mobile-history-list">
          {paginatedRecords.map((record) => (
            <div key={record.id} className={`mobile-record-card ${record.recordType === 'certificate' ? 'is-certificate' : ''}`}>
              <div className="card-header">
                <div className="date-info">
                  <span className="d-main">{new Date(record.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
                  <span className="d-year">{new Date(record.created_at).getFullYear()}</span>
                </div>
                <span className={`status-pill status-${record.status}`}>
                  {record.recordType === 'certificate' ? 'Certificate' : formatStatus(record.status)}
                </span>
              </div>
              
              <div className="card-body">
                <div className="patient-info">
                  <span className="p-name">{record.patient_name}</span>
                  <span className="p-details">{record.patient_age}y • {record.patient_gender}</span>
                </div>
                <div className="diagnosis-info">
                  <span className="label">{record.recordType === 'certificate' ? 'Purpose' : 'Diagnosis'}</span>
                  <p className="value">{record.recordType === 'certificate' ? record.purpose : (record.diagnosis || 'N/A')}</p>
                </div>
              </div>

              <div className="card-actions">
                <button className="mobile-action-btn view" onClick={() => handleViewRecord(record)}>
                  <Eye size={18} />
                  <span>View</span>
                </button>
                <button className="mobile-action-btn download" onClick={() => handleDownload(record)}>
                  <Download size={18} />
                  <span>PDF</span>
                </button>
                <button className="mobile-action-btn print" onClick={() => handlePrint(record)}>
                  <Printer size={18} />
                  <span>Print</span>
                </button>
                
                {record.recordType === 'prescription' && (
                  <>
                    {!['cancelled', 'dispensed'].includes(record.status) && (
                      <button className="mobile-action-btn edit" onClick={() => onEdit && onEdit(record)}>
                        <Edit size={18} />
                        <span>{['draft', 'in_progress'].includes(record.status) ? "Continue" : "Edit"}</span>
                      </button>
                    )}
                    {!['cancelled', 'completed', 'dispensed'].includes(record.status) && (
                      <button className="mobile-action-btn cancel" onClick={() => onCancel && onCancel(record.id)}>
                        <XCircle size={18} />
                        <span>Cancel</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {totalPages >= 1 && filteredRecords.length > 0 && (
          <div className="directory-pagination-v3">
            <div className="pagination-left">
              <span>Page {page} of {totalPages}</span>
            </div>

            <div className="pagination-center">
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className="btn-page-arrow-v3"
              >
                &lt; Previous
              </button>

              {renderPageNumbers()}

              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className="btn-page-arrow-v3"
              >
                Next &gt;
              </button>
            </div>

            <form onSubmit={handleGoToPage} className="pagination-right">
              <span>Go to</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={goToPageInput}
                onChange={(e) => setGoToPageInput(e.target.value)}
                placeholder="Page"
              />
              <button type="submit" className="btn-go-page">Page</button>
            </form>
          </div>
        )}

        {filteredRecords.length === 0 && (
          <div className="empty-state" style={{ padding: '4rem' }}>
            <History size={48} color="#cbd5e1" />
            <p style={{ marginTop: '1rem', color: '#64748b' }}>No records found matching the filters</p>
          </div>
        )}
      </div>

      {viewingPrescription && (
        <PrescriptionViewer
          prescription={viewingPrescription}
          mode={viewerMode}
          onClose={() => setViewingPrescription(null)}
        />
      )}

      {viewingCertificate && (
        <CertificateViewer 
          certificate={viewingCertificate}
          onClose={() => setViewingCertificate(null)}
        />
      )}
    </div>
  );
}

export default PrescriptionHistory;